import { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { HERO_IMAGE } from "../../data/machines";
import { EASE } from "../Reveal";
import { useSectionNav } from "../../lib/nav";

const LINES = [
  { text: "Tarımın Gücüne", accent: false },
  { text: "Güç Katıyoruz", accent: true },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const navigate = useNavigate();
  const location = useLocation();
  const nav = useSectionNav(navigate, location);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-ink-deep"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={HERO_IMAGE}
          alt="Baytaş Tarım makinesi tarlada toprak işliyor"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: EASE }}
          className="h-full w-full object-cover"
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink-deep via-ink-deep/35 to-ink-deep/10" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink-deep/85 via-ink-deep/25 to-transparent" />
      <div className="grain-overlay" aria-hidden="true" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-16 pt-32 sm:px-8 lg:pb-24">
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
          className="eyebrow-light"
          data-testid="hero-eyebrow"
        >
          Baytaş Tarım — Tarım Makineleri
        </motion.p>

        <h1
          className="mt-5 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-paper sm:text-5xl lg:text-6xl"
          data-testid="hero-title"
        >
          {LINES.map((line, i) => (
            <span key={line.text} className="mt-1 block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "text-amber" : "text-paper"}`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.4 + i * 0.14, ease: EASE }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
          className="mt-6 max-w-xl text-base leading-relaxed text-paper/80 sm:text-lg"
          data-testid="hero-subtitle"
        >
          Kaliteli, güçlü ve verimli tarım makineleriyle üretiminize değer katıyoruz.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1, ease: EASE }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <a
            href="#makineler"
            onClick={nav("makineler")}
            data-testid="hero-cta-machines"
            className="btn btn-primary"
          >
            Makinelerimizi İncele
          </a>
          <a
            href="#iletisim"
            onClick={nav("iletisim")}
            data-testid="hero-cta-contact"
            className="btn btn-ghost-light"
          >
            Bize Ulaşın
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.4 }}
        className="absolute bottom-8 right-8 z-10 hidden flex-col items-center gap-2 text-paper/60 lg:flex"
        aria-hidden="true"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Keşfet</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}